import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { WebAuth } from 'auth0-js';
import { CookieService } from 'ngx-cookie-service';
import { lastValueFrom } from 'rxjs';
import { AuthToken } from 'src/app/classes/authToken';
import { copy } from 'src/app/lib/copy';
import { sleep } from 'src/app/lib/sleep';
import { AppConfigService } from 'src/app/providers/config.service';
import { BrowserService } from 'src/app/services/browser.service';
import { DataLoaderService } from 'src/app/services/data-loader.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';

// temp
export interface LinkedAccountTemp {
  user_id: string;
  provider: string;
  connection: string;
  isSocial: boolean;
  profileData?: {
    email: string;
    email_verified: boolean;
    name: string;
    given_name: string;
    family_name: string;
    picture: string;
    locale: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loaded = false;
  webAuth: WebAuth;

  constructor(
    private state: StateService,
    private loader: DataLoaderService,
    private config: AppConfigService,
    private cookieService: CookieService,
    private storage: StorageService,
    private browserService: BrowserService,
    private http: HttpClient,
    private toasts: ToastService,
    private featureFlags: FeatureFlagsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.tokenInit();
    this.state.renewAuthToken = async () => {
      return this.renewAuthIfNeeded();
    };
  }

  init() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    if (this.loaded) return;
    this.webAuth = new WebAuth({
      domain: this.config.authDomain,
      clientID: this.config.authClientID,
      responseType: 'token id_token',
      scope: 'profile email openid',
      redirectUri: window.location.origin + '/auth/authorize',
    });
    this.loaded = true;
  }

  async tokenInit() {
    if (this.browserService.getTenantCustomDomain()) return;
    return this.loader.get('authToken', this.checkSession.bind(this));
  }

  private getRedirectUri(options: {
    returnUrl?: string;
    isPartOfSignup?: boolean;
    forwardOnly?: boolean;
    signupPurpose?: string;
  }) {
    let redirectUri = `${window.location.origin}/auth/authorize`;
    let queryParams: any = {};

    if (options.returnUrl) queryParams.returnUrl = options.returnUrl;
    if (options.isPartOfSignup) queryParams.signup = 'true';
    if (options.forwardOnly) queryParams.forwardOnly = 'true';

    if (options.signupPurpose) queryParams.signup_purpose = options.signupPurpose;
    if (options.signupPurpose === 'linking') queryParams.primary_token = this.state.tokenData.idToken;

    const queryString = new URLSearchParams(queryParams).toString();

    if (queryString) redirectUri += `?${queryString}`;
    return redirectUri;
  }

  async login(
    email: string,
    password: string,
    options: { returnUrl?: string; isPartOfSignup?: boolean; forwardOnly?: boolean } = {}
  ) {
    this.init();

    const isSso = await this.checkSsoDomain(email.split('@')[1]);
    if (isSso) return this.singleSignonRedirect(options.returnUrl, email);

    return new Promise((resolve, reject) => {
      const redirectUri = this.getRedirectUri(options);
      this.webAuth.login(
        {
          username: email,
          password,
          realm: 'Username-Password-Authentication',
          redirectUri,
        },
        async (err: any) => {
          if (err) {
            const isSso = await this.checkSsoDomain(email.split('@')[1]);
            if (isSso) reject('sso');
            else reject(err);
          } else resolve(null);
        }
      );
    });
  }

  async signup(email: string, password: string, ref?: string, signupPurpose?: string) {
    this.init();
    return new Promise((resolve, reject) => {
      let options = <any>{
        // any because for some reason the type here doesnt include the name fields (api accepts it)
        email,
        password,
        connection: 'Username-Password-Authentication',
        userMetadata: {},
      };

      if (ref) options.userMetadata.ref = ref;
      if (signupPurpose) options.userMetadata.signup_purpose = signupPurpose;

      this.webAuth.signup(options, (err: any) => {
        if (err) reject(err);
        else resolve(null);
      });
    });
  }

  loginGoogle(email?: string, returnUrl?: string, ref?: string, signupPurpose?: string, prompt?: string) {
    this.init();

    let redirectUri = `${window.location.origin}/auth/authorize`;

    let queryParams: any = {};

    if (returnUrl) queryParams.returnUrl = returnUrl;
    // Adding ref to the return url. The backend uses this to add it to the users metadata
    if (ref) queryParams.ref = ref;
    if (signupPurpose) queryParams.signup_purpose = signupPurpose;
    if (signupPurpose === 'linking') queryParams.primary_token = this.state.tokenData.idToken;

    let queryString = new URLSearchParams(queryParams).toString();
    if (queryString) redirectUri += `?${queryString}`;

    this.webAuth.authorize({
      connection: 'google-oauth2',
      login_hint: email,
      redirectUri,
      prompt,
    });
  }

  logout(message?: string) {
    this.init();
    this.webAuthLogout(message);
    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');
    this.removeToken();
  }

  webAuthLogout(message?: string) {
    this.cookieService.delete('picflow-loggedin', '/');
    let returnTo = `${window.location.origin}/auth/login`;
    if (message) returnTo += '?message=' + encodeURIComponent(message);
    this.webAuth.logout({ returnTo });
  }

  async checkSession() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    this.init();

    let resp = await this.webAuthCheckSession();
    if (!resp) return false;

    resp.provider = resp.idTokenPayload.sub.split('|')[0];

    this.state.tokenData = resp;

    resp.expiresAt = resp.idTokenPayload.exp * 1000;
    console.debug('Got new token', copy(resp));

    return resp;
  }

  public async getTokenData() {
    await this.tokenInit();
    return this.state.tokenData;
  }

  public async renewAuthIfNeeded() {
    if (!this.state.tokenData) return;
    let timeLeft = this.state.tokenData.expiresAt - Date.now();
    if (timeLeft > 1000 * 60 * 1) return;
    // If there is less than 1 minute(s) left of this token then we renew it
    console.debug('Renewing token', copy(this.state.tokenData));
    this.loader.remove('authToken');
    const resp = await this.checkSession();
    if (!resp) {
      this.toasts.add('Authentication expired, please login again', 'error');
      await sleep(1000);
      this.logout();
    }
  }

  public async getLinkedAccounts(): Promise<LinkedAccountTemp[]> {
    return this.loader.get('linkedAccounts', async () => {
      return await lastValueFrom(this.http.get<LinkedAccountTemp[]>(`${this.config.apiEndpoint}/auth/identities`));
    });
  }

  public async connectSocialAccount() {
    this.loginGoogle(this.state.user.email, '/a/galleries?message=socialalreadyexists', undefined, 'linking', 'login');
    this.loader.remove('linkedAccounts');
  }

  public async connectSocialAccountConfirm(session_token: string) {
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/auth/identities`, { session_token }));
    this.loader.remove('linkedAccounts');
  }

  public async disconnectSocialAccount(account: LinkedAccountTemp) {
    await lastValueFrom(
      this.http.delete(`${this.config.apiEndpoint}/auth/identities/${account.provider}|${account.user_id}`)
    );
    this.loader.remove('linkedAccounts');
  }

  public async addPasswordToSocialAccount(password: string) {
    const email = this.state.user.email;
    try {
      await this.signup(email, password, undefined, 'linking');
    } catch (err) {
      const error = err.description;
      if (error !== 'Invalid sign up') throw err;
    }
    const returnUrl = `/auth/confirm-add-password?social_token=${this.state.tokenData.idToken}`;
    await this.login(email, password, { returnUrl, forwardOnly: true });
  }

  public async addPasswordToSocialAccountConfirm(socialAccesstoken: string) {
    await lastValueFrom(
      this.http.post(`${this.config.apiEndpoint}/auth/identities/relink_social`, { auth_token: socialAccesstoken })
    );
  }

  async resetPassword(email: string) {
    return new Promise(resolve => {
      this.webAuth.changePassword({ connection: 'Username-Password-Authentication', email }, e => resolve(e));
    });
  }

  private removeToken() {
    this.state.tokenData = undefined;
  }

  public async checkSsoDomain(domain: string) {
    try {
      await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/connections/by_domain/${domain}`));
      return true;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      return false;
    }
  }

  public singleSignonRedirect(returnUrl: string, email?: string, signupPurpose?: string) {
    const redirectUri = this.getRedirectUri({ returnUrl, signupPurpose });
    this.webAuth.authorize({
      redirectUri,
      login_hint: email,
    });
  }

  private webAuthCheckSession(): Promise<AuthToken> {
    return new Promise(resolve => {
      this.webAuth.checkSession({}, (err: any, data: AuthToken) => {
        if (err) {
          console.warn(err);
          resolve(undefined);
        } else resolve(data);
      });
    });
  }
}
